import React, { useState, useEffect } from 'react';
import { FiLink, FiShield, FiClock, FiHash, FiCopy, FiCheck, FiAlertCircle, FiLock, FiZap } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useStore } from '../../store';
import { useAuth } from '../../contexts/AuthContext';
import GuestModeBanner from '../GuestModeBanner';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const UrlShortener: React.FC = () => {
  const { createShortUrl, isCreating, settings } = useStore();
  const { user, isGuest, usage, initializeGuest, refreshUsage } = useAuth();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [customAlias, setCustomAlias] = useState('');
  const [password, setPassword] = useState('');
  const [burnAfterReading, setBurnAfterReading] = useState(false);
  const [expiresIn, setExpiresIn] = useState<'1d' | '7d' | '30d' | 'never'>(() => {
    // Set default expiration based on tier
    if (isGuest) return '7d';
    if (user?.tier === 'free') return '30d';
    return settings.defaultExpiration;
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [showGuestBanner, setShowGuestBanner] = useState(true);

  // Initialize guest session if needed
  useEffect(() => {
    if (!user && !isGuest) {
      initializeGuest();
    }
  }, [user, isGuest, initializeGuest]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }

    // Check if guest has reached limit
    if (isGuest && usage && usage.remainingLinks === 0) {
      toast.error('You\'ve reached the guest limit. Please create an account to continue.');
      navigate('/register');
      return;
    }

    // Check if user needs Pro for custom alias
    if (customAlias && user?.tier === 'free') {
      const hasCustomAliasFeature = false; // This should check actual feature access
      if (!hasCustomAliasFeature) {
        toast.error('Custom aliases require a Pro subscription');
        return;
      }
    }

    try {
      const shortUrl = await createShortUrl(url, {
        customAlias: customAlias || undefined,
        expiresIn,
        password: password || undefined,
        burnAfterReading: burnAfterReading || undefined,
      });
      
      setResult(shortUrl);
      setUrl('');
      setCustomAlias('');
      setPassword('');
      setBurnAfterReading(false);
      
      // Generate QR code
      if (shortUrl.shortUrl) {
        try {
          const qrDataUrl = await QRCode.toDataURL(shortUrl.shortUrl, {
            errorCorrectionLevel: 'M',
            type: 'image/png',
            margin: 1,
            color: {
              dark: '#1E40AF',
              light: '#FFFFFF',
            },
            width: 256,
          });
          setQrCodeUrl(qrDataUrl);
        } catch (err) {
          console.error('Failed to generate QR code:', err);
        }
      }
      
      toast.success('Short URL created successfully!');
      
      // Refresh usage after creating a link
      if (refreshUsage) {
        refreshUsage();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create short URL');
    }
  };

  const copyToClipboard = async () => {
    if (result) {
      await navigator.clipboard.writeText(result.shortUrl);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getPrivacyScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div>
      {/* Guest mode banner */}
      {isGuest && showGuestBanner && (
        <GuestModeBanner onDismiss={() => setShowGuestBanner(false)} />
      )}
      
      <div className="card">
        {/* Usage indicator for authenticated users */}
        {user && !isGuest && usage && user.tier !== 'pro' && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FiAlertCircle className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm text-gray-700">
                  {usage.remainingLinks === -1 
                    ? 'Unlimited links available'
                    : `${usage.remainingLinks} links remaining this month`}
                </span>
              </div>
              {user.tier === 'free' && (
                <button
                  onClick={() => navigate('/upgrade')}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Upgrade to Pro
                </button>
              )}
            </div>
            {usage.linksLimit !== -1 && (
              <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-primary-600 h-1.5 rounded-full transition-all"
                  style={{ width: `${(usage.linksCreated / usage.linksLimit) * 100}%` }}
                />
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
        {/* URL Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter your URL
          </label>
          <div className="relative">
            <FiLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/very-long-url-that-needs-shortening"
              className="input pl-10"
              required
            />
          </div>
        </div>

        {/* Advanced Options Toggle */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          {showAdvanced ? '− Hide' : '+ Show'} Advanced Options
        </button>

        {/* Advanced Options */}
        {showAdvanced && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            {/* Custom Alias */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiHash className="inline w-4 h-4 mr-1" />
                Custom Alias {user?.tier !== 'pro' && (
                  <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                    Pro Feature
                  </span>
                )}
              </label>
              {user?.tier === 'pro' ? (
                <>
                  <input
                    type="text"
                    value={customAlias}
                    onChange={(e) => setCustomAlias(e.target.value)}
                    placeholder="my-custom-link"
                    className="input"
                    pattern="[a-zA-Z0-9-]+"
                    minLength={3}
                    maxLength={50}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Letters, numbers, and hyphens only (3-50 characters)
                  </p>
                </>
              ) : (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="text-sm text-gray-600 mb-2">
                    Create memorable custom links with your own aliases
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/upgrade')}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Upgrade to Pro to unlock →
                  </button>
                </div>
              )}
            </div>

            {/* Password Protection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiLock className="inline w-4 h-4 mr-1" />
                Password Protection {isGuest && (
                  <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                    Requires Account
                  </span>
                )}
              </label>
              {!isGuest ? (
                <>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Optional password (min 6 characters)"
                    className="input"
                    minLength={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Add a password to protect your link (optional)
                  </p>
                </>
              ) : (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="text-sm text-gray-600 mb-2">
                    Protect your links with passwords for extra security
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Create an account to unlock →
                  </button>
                </div>
              )}
            </div>

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiClock className="inline w-4 h-4 mr-1" />
                Link Expiration
              </label>
              <div className="grid grid-cols-4 gap-2">
                {(() => {
                  // Determine available options based on tier
                  let options: Array<'1d' | '7d' | '30d' | 'never'>;
                  if (isGuest) {
                    options = ['1d', '7d'];
                  } else if (user?.tier === 'free') {
                    options = ['1d', '7d', '30d'];
                  } else {
                    options = ['1d', '7d', '30d', 'never'];
                  }
                  
                  return options.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setExpiresIn(option)}
                      className={clsx(
                        'py-2 px-3 rounded-lg border transition-all',
                        expiresIn === option
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:border-gray-400'
                      )}
                    >
                      {option === 'never' ? 'Never' : option === '1d' ? '1 Day' : option === '7d' ? '7 Days' : '30 Days'}
                    </button>
                  ));
                })()}
              </div>
              {(isGuest || user?.tier === 'free') && (
                <p className="text-xs text-gray-500 mt-2">
                  {isGuest ? 'Guest links expire in max 7 days' : 'Upgrade to Pro for links that never expire'}
                </p>
              )}
            </div>

            {/* Burn After Reading */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiZap className="inline w-4 h-4 mr-1" />
                Auto-Expiring Link {isGuest && (
                  <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                    Requires Account
                  </span>
                )}
              </label>
              {!isGuest ? (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={burnAfterReading}
                      onChange={(e) => setBurnAfterReading(e.target.checked)}
                      className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        Burn after reading
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        Link will self-destruct after first click. Perfect for sharing passwords or sensitive data.
                      </p>
                    </div>
                  </label>
                  {burnAfterReading && (
                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-xs text-orange-700 flex items-start">
                        <FiAlertCircle className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                        This link will only work once. After the first visit, it will be permanently deleted.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="text-sm text-gray-600 mb-2">
                    Create self-destructing links that disappear after one use
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Create an account to unlock →
                  </button>
                </div>
              )}
            </div>

            {/* Privacy Options */}
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.enableTrackerBlocking}
                  disabled
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">
                  <FiShield className="inline w-4 h-4 mr-1 text-green-500" />
                  Tracker Blocking Enabled
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isCreating}
          className="btn btn-primary w-full flex items-center justify-center space-x-2"
        >
          <FiShield className="w-4 h-4" />
          <span>{isCreating ? 'Creating...' : 'Shorten & Protect'}</span>
        </button>
        </form>

        {/* Result Card */}
        {result && (
        <div className="mt-6 p-6 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg border border-primary-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Your Protected Short Link
              </h3>
              
              {/* Short URL */}
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="text"
                  value={result.shortUrl}
                  readOnly
                  className="flex-1 px-3 py-2 bg-white rounded-lg border border-gray-300 font-mono text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  className="btn btn-secondary flex items-center space-x-1"
                >
                  {copied ? <FiCheck className="w-4 h-4" /> : <FiCopy className="w-4 h-4" />}
                  <span>{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>

              {/* Privacy Score */}
              {result.privacyScore !== undefined && (
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-gray-600">Privacy Score:</span>
                  <span className={clsx('font-semibold', getPrivacyScoreColor(result.privacyScore))}>
                    {result.privacyScore}/100
                  </span>
                  {result.osintAnalysis?.risks && result.osintAnalysis.risks.length > 0 && (
                    <span className="text-xs text-gray-500">
                      ({result.osintAnalysis.risks.length} risks mitigated)
                    </span>
                  )}
                </div>
              )}

              {/* Features */}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="badge badge-success">
                  No IP Logging
                </span>
                <span className="badge badge-success">
                  Trackers Blocked
                </span>
                {result.isPasswordProtected && (
                  <span className="badge badge-primary">
                    <FiLock className="w-3 h-3 mr-1" />
                    Password Protected
                  </span>
                )}
                {result.burnAfterReading && (
                  <span className="badge badge-orange">
                    <FiZap className="w-3 h-3 mr-1" />
                    Burns After First Click
                  </span>
                )}
                {expiresIn !== 'never' && !result.burnAfterReading && (
                  <span className="badge badge-warning">
                    Expires in {expiresIn}
                  </span>
                )}
              </div>
            </div>

            {/* QR Code */}
            {qrCodeUrl && (
              <div className="ml-4">
                <img
                  src={qrCodeUrl}
                  alt="QR Code"
                  className="w-24 h-24"
                />
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default UrlShortener;